// 📄 pages/Proyectos/components/IncidenteCard.tsx
import React from 'react';
import { Paperclip, ChevronRight, AlertCircle, User, CheckCircle2 } from 'lucide-react';
import { Incidente, CANALES_INTEGRACION } from '../../../services/incidenteService.ts';
import { ProjectMember } from '../../../services/projectService.ts';
import { SOLUCIONES } from '../solucionesConfig.ts';
import { PRIORIDAD_STYLES, ESTADO_STYLES, AMBIENTE_STYLES } from './incidenteStyles.ts';

interface IncidenteCardProps {
  incidente: Incidente;
  miembros: ProjectMember[];
  onClick: () => void;
}

export default function IncidenteCard({ incidente, miembros, onClick }: IncidenteCardProps) {
  const solucionLabel = SOLUCIONES[incidente.solucionRelacionada]?.label || incidente.solucionRelacionada;
  const asignado = miembros.find(m => m.userId === incidente.asignadoA);
  const fecha = incidente.fechaReporte?.toDate?.();
  const canalLabel = incidente.canal ? CANALES_INTEGRACION.find(c => c.value === incidente.canal)?.label : undefined;
  const documentado = !!(incidente.causaRaiz || incidente.errorConocidoRef);

  return (
    <div
      onClick={onClick}
      className="group flex items-start gap-4 bg-white/5 border border-white/10 rounded-2xl p-4 cursor-pointer transition-all duration-150 hover:border-blue-400 hover:-translate-y-0.5 hover:shadow-lg"
    >
      <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-[rgba(91,155,240,0.12)]">
        <AlertCircle className="w-5 h-5 text-blue-400" />
      </div>

      <div className="flex-1 min-w-0 space-y-1.5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-white truncate">{incidente.titulo}</span>
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${PRIORIDAD_STYLES[incidente.prioridad]}`}>
            {incidente.prioridad}
          </span>
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${ESTADO_STYLES[incidente.estado]}`}>
            {incidente.estado}
          </span>
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${AMBIENTE_STYLES[incidente.ambiente]}`}>
            {incidente.ambiente}
          </span>
          {documentado && (
            <span
              className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
              title="Tiene causa raíz y/o error conocido vinculado"
            >
              <CheckCircle2 className="w-3 h-3" /> Documentado
            </span>
          )}
          {incidente.categoriaSR && (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
              SR &middot; {incidente.categoriaSR}
            </span>
          )}
          {incidente.sistema && (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border border-white/10 bg-white/5 text-gray-300">
              {incidente.sistema}
            </span>
          )}
        </div>

        <div className="flex items-center gap-3 flex-wrap text-xs text-gray-400">
          <span>{solucionLabel}</span>
          {canalLabel && (
            <>
              <span className="text-gray-600">&middot;</span>
              <span>{canalLabel}</span>
            </>
          )}
          {fecha && (
            <>
              <span className="text-gray-600">&middot;</span>
              <span>{fecha.toLocaleDateString()}</span>
            </>
          )}
          {asignado && (
            <>
              <span className="text-gray-600">&middot;</span>
              <span className="flex items-center gap-1"><User className="w-3 h-3" /> {asignado.email}</span>
            </>
          )}
        </div>
      </div>

      {incidente.adjuntos.length > 0 && (
        <div className="flex items-center gap-1 text-gray-500 flex-shrink-0" title={`${incidente.adjuntos.length} adjunto(s)`}>
          <Paperclip className="w-4 h-4" />
        </div>
      )}

      <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-blue-400 transition-colors flex-shrink-0" />
    </div>
  );
}
